import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import Button from '../../../core/components/Button';
import CheckboxGroup from '../../../core/components/CheckboxGroup';
import SelectField from '../../../core/components/Fields/SelectField';
import StringField from '../../../core/components/Fields/StringField';
import FormField from '../../../core/components/FormField';
import Layout from '../../../core/components/Layout';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries';
import Page from '../../../core/components/Page';
import SaveStatus from '../../../core/components/SaveStatus';
import SettingsFormCard from '../../../core/components/SettingsFormCard';
import SettingsNavigation from '../../../core/components/SettingsNavigation';
import { useToaster } from '../../../core/components/Toast';
import countFieldErrors from '../../../core/utils/countFieldErrors';
import { url } from '../../../core/utils/link';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_OUTLOOK_PATH } from '../../constants';
import { GET_OUTLOOK_ADDINS, UPDATE_OUTLOOK_ADDIN } from '../../queries';

const FORM_DEFAULT_VALUES = {
    name: '',
    channelMode: 'MANUAL',
    orderChannelIds: [],
    invoiceChannelIds: [],
    orderConfirmationChannelIds: [],
    rfqChannelIds: [],
    deliveryNoteChannelIds: [],
};
const CHANNEL_MODE_OPTIONS = [
    { value: 'ALL', label: 'All Channels' },
    { value: 'ADDIN', label: 'Add-In' },
    { value: 'MANUAL', label: 'Manual' },
];

const OutlookIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { integrationId } = useParams();

    const { data, refetch } = useQuery(GET_OUTLOOK_ADDINS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const addIn = data?.outlookAddins?.find((a) => a.id === integrationId);
    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    const channelMode = form.watch('channelMode');
    const [updateAddIn, { loading: isSubmitting }] = useMutation(UPDATE_OUTLOOK_ADDIN);

    useEffect(() => {
        if (addIn) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: addIn.name,
                channelMode: addIn.channelMode || 'MANUAL',
                orderChannelIds: addIn.orderChannels.map((channel) => channel.id),
                invoiceChannelIds: addIn.invoiceChannels.map((channel) => channel.id),
                orderConfirmationChannelIds: addIn.orderConfirmationChannels.map((channel) => channel.id),
                rfqChannelIds: addIn.rfqChannels.map((channel) => channel.id),
                deliveryNoteChannelIds: addIn.deliveryNoteChannels.map((channel) => channel.id),
            });
        }
    }, [addIn]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const deliveryNoteChannels = channelData?.deliveryNoteProcessingChannels || [];
    const backTooltipText = `${t('backTooltipText')} ${t('outlook.title')}`;
    const handleUpdateAddIn = async (formData) => {
        try {
            await updateAddIn({
                variables: {
                    id: addIn.id,
                    name: formData.name,
                    channelMode: formData.channelMode,
                    orderChannels: formData.orderChannelIds,
                    invoiceChannels: formData.invoiceChannelIds,
                    orderConfirmationChannels: formData.orderConfirmationChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                    deliveryNoteChannels: formData.deliveryNoteChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('outlook.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_OUTLOOK_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('outlook.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('outlook.title'), to: url(CUSTOMER_INTEGRATION_OUTLOOK_PATH) },
                            { label: addIn?.name || t('outlook.form.updateTitle') },
                        ]}
                    />
                </Page.Header>
                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleUpdateAddIn)}
                        title={t('outlook.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            rules={{ required: t('outlook.form.nameRequired') }}
                            render={({ field }) => (
                                <FormField label={t('outlook.form.name')} required={true} error={formErrors.name}>
                                    <StringField required={true} {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="channelMode"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('outlook.form.channelMode')}
                                    helpText={t(`outlook.form.channelModeHelp.${field.value}`)}
                                >
                                    <SelectField
                                        {...field}
                                        required
                                        options={CHANNEL_MODE_OPTIONS.map((option) => ({
                                            ...option,
                                            label: t(`outlook.form.channelModeOptions.${option.value}`),
                                            description: t(`outlook.form.channelModeHelp.${option.value}`),
                                        }))}
                                    />
                                </FormField>
                            )}
                        />

                        {channelMode !== 'ALL' && (
                            <>
                                <Controller
                                    name="orderChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('outlook.form.orderChannelIds')}
                                            error={formErrors.orderChannelIds}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={
                                                    orderChannels.map((channel) => ({
                                                        value: channel.id,
                                                        label: channel.name,
                                                    })) || []
                                                }
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="invoiceChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('outlook.form.invoiceChannelIds')}
                                            error={formErrors.invoiceChannelIds}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={
                                                    invoiceChannels.map((channel) => ({
                                                        value: channel.id,
                                                        label: channel.name,
                                                    })) || []
                                                }
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="orderConfirmationChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('outlook.form.orderConfirmationChannelIds')}
                                            error={formErrors.orderConfirmationChannelIds}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={
                                                    orderConfirmationChannels.map((channel) => ({
                                                        value: channel.id,
                                                        label: channel.name,
                                                    })) || []
                                                }
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="rfqChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('outlook.form.rfqChannelIds')}
                                            error={formErrors.rfqChannelIds}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={
                                                    rfqChannels.map((channel) => ({
                                                        value: channel.id,
                                                        label: channel.name,
                                                    })) || []
                                                }
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="deliveryNoteChannelIds"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('outlook.form.deliveryNoteChannelIds')}
                                            error={formErrors.deliveryNoteChannelIds}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={
                                                    deliveryNoteChannels.map((channel) => ({
                                                        value: channel.id,
                                                        label: channel.name,
                                                    })) || []
                                                }
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            </>
                        )}
                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_OUTLOOK_PATH)}>
                                    {t('outlook.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OutlookIntegrationEditPage;
